import React, { useCallback, useMemo, useRef, useState } from "react";

import EventService from "service/event/EventService";
import CallNoteModal from "modules/patient/components/CallNoteModal";
import { callNoteVariant } from "modules/patient/helper/callNoteVariant";
import { overrideMemberId, bookedMemberId } from "modules/patient/helper/bookingFor";
import AppointmentService from "../service/AppointmentService";
import CancelAppointmentDialog from "../components/CancelAppointmentDialog";
import {
  opensCallNote,
  REASON_REQUIRED_STATUSES,
  STATUS_LABEL,
} from "../helper/appointmentConstants";

/**
 * ONE appointment status-change flow, shared by every screen that offers the
 * control — the dashboard, the patient screen, the appointment-details screen
 * and Create Rx — so a status change behaves the same wherever a doctor makes it.
 *
 * REFUNDS ARE THE EXPERT DASHBOARD'S, BY DECISION (Nittin, 2026-09-14: "make
 * refund related stuff same as expert's", with the reason kept and all four
 * screens changed). phr offers no refund choice, no refund amount and no force
 * refund, and it sends NO `refundRequest` — exactly as the expert dashboard's
 * AppointmentDetails does ({appointmentID, status}). The MONEY OUTCOME IS THEN
 * THE SERVER'S: an omitted refundRequest decodes to UNKNOWN(0), and
 * api-appointment-service appointment_controller.go (~4134 on zylahealth
 * release 727042d; ~4138 on its phr branch, same code) starts createAutoRefund
 * for 0 on a cancel-family status. So, as on the expert dashboard:
 *   · a paid appointment is refunded in FULL (the whole remaining balance),
 *     recorded as created by the doctor — including a tele or video
 *     consultation cancelled at the last minute or marked No show;
 *   · the one exception is an IN-PERSON consultation or a VACCINE inside the
 *     rescheduling cutoff before its slot, or past it (determineRefundStatus ->
 *     Tentative): not refunded;
 *   · one already fully refunded raises a refund task for ops;
 *   · nothing is refunded when the primary payment order is missing or unpaid.
 * This replaced bridge's two-step refund fork (re-read, choose the money
 * outcome, typed REFUND confirmation). It is deliberate, not an omission — do
 * not "restore" refundRequest without asking.
 *
 * WHAT phr KEEPS that the expert dashboard does not: a MANDATORY REASON for the
 * four cancel-family statuses. The server writes `updateReason` and the audit
 * trail unconditionally, so a status change with no reason blanks whatever was
 * stored — which is exactly what the expert dashboard does today.
 *
 * THE FLOW:
 *   1. Any other status is sent at once.
 *   2. Cancelled / No show / Declined / Not eligible open the reason dialog;
 *      Confirm sends { appointmentID, reportUrl: "", status, reason }.
 *
 * THE CALL NOTE COMES WITH IT. A status change that lands is the one moment a
 * call outcome is known, so the note opens itself on the status just set — and
 * it opens HERE, in the flow, rather than in each screen. It shipped wired into
 * the patient screen alone, so changing a status anywhere else — the
 * appointments dashboard, the appointment-details screen, Create Rx — silently
 * skipped it (Nittin, 2026-09-24). A fifth screen adopting this hook gets it
 * without knowing it exists.
 *
 * Only on SUCCESS: a failed change knows nothing, and a note about an outcome
 * that did not happen is worse than no note.
 *
 * @param {object}   opts
 * @param {function} opts.onApplied  (status, appointment) after a change lands.
 * @param {function} [opts.onError]  Defaults to the app's error toast.
 * @param {object}   [opts.patient]  The screen's patient, when it holds one.
 *   Without it the note is keyed on the appointment itself, which is all the
 *   dashboard's rows carry — and the note's copy reads the name off the row.
 * @param {boolean}  [opts.callNote=true]  false for a screen that renders the
 *   note ITSELF: the patient screen keeps it inside its header card, beside the
 *   manual "Call note" button, and two of them would open at once.
 */
const useStatusChange = ({ onApplied, onError, patient, callNote = true } = {}) => {
  const service = useRef(new AppointmentService()).current;

  /** The id being written, or null. Screens disable their control on it. */
  const [updatingId, setUpdatingId] = useState(null);
  /** { appointment, statusToApply } while the reason dialog is open. */
  const [confirming, setConfirming] = useState(null);
  /** { appointment, status } while the call note is open; null when closed. */
  const [noteFor, setNoteFor] = useState(null);

  const fail = useCallback(
    (message) => {
      if (onError) onError(message);
      else EventService.emit("showError", { message });
    },
    [onError]
  );

  const applyStatus = useCallback(
    (appointment, status, reason) => {
      setUpdatingId(appointment.id);
      service.updateStatus(
        appointment.id,
        status,
        () => {
          setUpdatingId(null);
          if (onApplied) onApplied(status, appointment);
          if (callNote && opensCallNote(appointment, status)) setNoteFor({ appointment, status });
        },
        (err) => {
          setUpdatingId(null);
          fail(`Could not update status: ${(err && err.message) || ""}`);
        },
        reason
      );
    },
    [service, onApplied, fail, callNote]
  );

  const changeStatus = useCallback(
    (appointment, status) => {
      if (!appointment) return;
      if (REASON_REQUIRED_STATUSES.indexOf(status) === -1) {
        applyStatus(appointment, status);
        return;
      }
      setConfirming({ appointment, statusToApply: status });
    },
    [applyStatus]
  );

  const confirm = useCallback(
    (reason) => {
      if (!confirming) return;
      const { appointment, statusToApply } = confirming;
      setConfirming(null);
      applyStatus(appointment, statusToApply, reason);
    },
    [confirming, applyStatus]
  );

  /* Rendered by the caller. Returning a node rather than asking four screens to
     each mount the dialog with the right props is what keeps them identical. */
  const dialogs = useMemo(
    () => (
      <>
        {confirming ? (
          <CancelAppointmentDialog
            appointment={confirming.appointment}
            statusToApply={confirming.statusToApply}
            onConfirm={confirm}
            onClose={() => setConfirming(null)}
          />
        ) : null}

        {noteFor ? (
          <CallNoteModal
            /* The variant is the TONE; the words are the status's own label, so
               a status nobody mapped still names itself. */
            variant={callNoteVariant(noteFor.status)}
            statusLabel={STATUS_LABEL[noteFor.status]}
            /* The screen's patient when it has one. Otherwise the person the
               appointment is FOR — phr's mapping first, then bookedFor, then
               the holder (helper/bookingFor) — which is the same person the
               BOOKED FOR cell names. The note's copy falls back to the row's
               patientName, so an id is enough. */
            patient={
              patient && patient.id
                ? patient
                : { id: overrideMemberId(noteFor.appointment) || bookedMemberId(noteFor.appointment) }
            }
            appointment={noteFor.appointment}
            onClose={() => setNoteFor(null)}
          />
        ) : null}
      </>
    ),
    [confirming, confirm, noteFor, patient]
  );

  return { changeStatus, updatingId, dialogs };
};

export default useStatusChange;
